import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Heart, Award, BookOpen } from 'lucide-react';
import { useSiteSettings } from '@/contexts/SiteSettingsContext';
import { db } from '@/lib/firebase';
import { doc, getDoc } from 'firebase/firestore';

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } };

const defaultAbout = {
  heading: 'Meet Your Singing Teacher',
  body: "Welcome to Singvolve! I'm Catherine, a passionate and experienced singing teacher based in Bristol.\nI believe every voice has a unique story to tell, and my mission is to help you discover and develop yours.\n\nWith years of experience teaching children and adults of all abilities, I create a warm, encouraging\nenvironment where students feel safe to explore their voice, take risks, and grow in confidence.\n\nWhether you're a complete beginner dreaming of singing your favourite songs, a parent looking for\nquality vocal tuition for your child, or an experienced singer wanting to refine your technique,\nI'm here to guide you every step of the way.\n\nLessons take place at the beautiful Bristol Cathedral Choir School, providing an inspiring setting\nfor musical development. I offer one-to-one lessons tailored entirely to your goals, interests, and pace.",
};

export default function About() {
  const settings = useSiteSettings();
  const [about, setAbout] = useState(defaultAbout);

  useEffect(() => {
    async function load() {
      try {
        const snap = await getDoc(doc(db, 'pages', 'about'));
        if (snap.exists()) {
          setAbout(prev => ({ ...prev, ...snap.data() }));
        }
      } catch (e) { /* use defaults */ }
    }
    load();
  }, []);

  const paragraphs = about.body.split(/\n\n+/).map(p => p.replace(/\n/g, ' ').trim()).filter(Boolean);

  return (
    <div data-testid="about-page" className="bg-white">
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          <motion.div initial="hidden" animate="visible" variants={fadeUp} transition={{ duration: 0.6 }}>
            <span className="inline-block px-4 py-1.5 rounded-full bg-[#F5F0F7] text-[#4B1D5E] text-sm font-medium mb-6">About Singvolve</span>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold text-[#2D1B36] mb-8">{about.heading}</h1>
            <div className="space-y-5 text-[#5E4B66] leading-relaxed">
              {paragraphs.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.2 }}>
            <div className="rounded-3xl overflow-hidden shadow-xl mb-8">
              <img
                src={settings.images?.about}
                alt="Singing lesson in progress"
                className="w-full h-[400px] object-cover"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              {[
                { icon: Heart, label: 'Supportive', desc: 'Warm, encouraging environment' },
                { icon: Award, label: 'Experienced', desc: 'Years of vocal teaching' },
                { icon: BookOpen, label: 'Tailored', desc: 'Lessons shaped to you' },
              ].map((item, i) => (
                <div key={i} className="bg-[#F5F0F7] rounded-2xl p-4 text-center">
                  <item.icon className="h-6 w-6 text-[#4B1D5E] mx-auto mb-2" strokeWidth={1.5} />
                  <p className="font-semibold text-sm text-[#2D1B36]">{item.label}</p>
                  <p className="text-xs text-[#8A7E90] mt-1">{item.desc}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
